import React, { FunctionComponent, useEffect, useState } from 'react'
import { useSubscriptionOffers } from 'SubscriptionOffers/SubscriptionOffersProvider'
import { useTranslation } from 'react-i18next'
import { useMessage } from 'Elements/Message'
import { ISubscriptionOffersListOfferData, useApi } from 'components/providers/ApiProvider'
import ListSkeleton from 'Elements/ListSkeleton'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import ListNoRecords from 'Elements/ListNoRecords'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import ListTablePagination from 'Elements/ListTablePagination'
import TableBody from '@material-ui/core/TableBody'
import { formatMoney, formatTimestamp } from 'utils'
import ListTableRow from 'Elements/ListTableRow'
import { Badge, Tooltip } from '@material-ui/core'
import IconInfo from '@material-ui/icons/InfoOutlined'
import AutoRenewIcon from '@material-ui/icons/Autorenew'
import ClientsDrawer, { ClientsDrawerData } from 'SubscriptionOffers/partials/ClientsDrawer'
import IconButton from '@material-ui/core/IconButton'
import IconEditClients from '@material-ui/icons/Group'
import IconEdit from '@material-ui/icons/Edit'
import EditSubscriptionOfferDialog, { EditSubscriptionOfferDialogData } from 'SubscriptionOffers/partials/EditSubscriptionOfferDialog'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const { t } = useTranslation()
  const api = useApi()
  const { showError } = useMessage()
  const { filter, loadList, listLoading, setListLoading, listPerPage, onChangePerPage, listPage, setListPage, onChangePage } = useSubscriptionOffers()

  const [offers, setOffers] = useState<{ items: ISubscriptionOffersListOfferData[], total: number }>(null)
  const [clientsDrawerData, setClientsDrawerData] = useState<ClientsDrawerData>(null)
  const [editSubscriptionOfferDialogData, setEditSubscriptionOfferDialogData] = useState<EditSubscriptionOfferDialogData>(null)

  const load = (onLoad?: Function, page?: number) => {
    if (!page) {
      setListLoading(true)
    }

    api.getSubscriptionOffersList({
      zone_id: filter.zone || undefined,
      type_id: filter.type || undefined,
      client: filter.client || undefined,
      limit: listPerPage,
      page: page || listPage,
    })
      .then(data => {
        if (!page && data.offers && !data.offers.length && data.totalOffers) {
          const pages = Math.ceil(data.totalOffers / listPerPage)

          if (listPage > pages) {
            setListPage(pages)
            load(onLoad, pages)

            return
          }
        }

        setListLoading(false)

        if (onLoad) {
          onLoad(data.offers)
        }

        setOffers({
          items: data.offers,
          total: data.totalOffers,
        })
      })
      .catch(error => {
        setListLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [loadList])

  const handleEditClients = (offer: ISubscriptionOffersListOfferData) => {
    setClientsDrawerData({
      id: offer.id,
    })
  }

  const handleEdit = (offer: ISubscriptionOffersListOfferData) => {
    setEditSubscriptionOfferDialogData({
      id: offer.id,
      price: offer.price,
      validFrom: offer.valid_from,
      validTo: offer.valid_to,
      maxActiveSubscriptions: offer.max_active_subscriptions,
      autoRenew: offer.auto_renew,
    })
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    onChangePage(page + 1)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangePerPage(parseInt(event.target.value, 10))
  }

  const nowTime = Date.now()

  return (
    <>
      {!offers && <ListSkeleton />}

      {offers && listLoading && <ListLoadingIndicator />}

      {offers && !offers.items.length && (
        <ListNoRecords />
      )}

      {offers && !!offers.items.length && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('zone')}</TableCell>
                  <TableCell>{t('type')}</TableCell>
                  <TableCell>{t('price')}</TableCell>
                  <TableCell>{t('period')}</TableCell>
                  <TableCell style={{ width: '5%', lineHeight: 1.2 }}>{t('maxActiveSubscriptions')}</TableCell>
                  <TableCell style={{ width: '5%', lineHeight: 1.2 }}>{t('maxActiveSessions')}</TableCell>
                  <TableCell>{t('clients')}</TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.items.map((offer, key) => {
                  const fade = offer.valid_to && new Date(offer.valid_to).getTime() <= nowTime

                  return (
                    <ListTableRow key={key} {...(fade && { fade: true })}>
                      <TableCell>
                        {offer.zone.code}
                      </TableCell>
                      <TableCell>
                        {offer.type.name}
                        {offer.auto_renew && (
                          <Tooltip title={t('withAutoRenew')} placement="top" arrow={true}>
                            <AutoRenewIcon fontSize="small" color="primary" style={{ verticalAlign: 'top', marginLeft: 2 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        {formatMoney(offer.price)}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(offer.valid_from, 'dd.MM.yyyy')} - {offer.valid_to ? formatTimestamp(offer.valid_to, 'dd.MM.yyyy') : '...'}
                      </TableCell>
                      <TableCell>
                        {offer.max_active_subscriptions !== null ? offer.max_active_subscriptions : '∞'}
                      </TableCell>
                      <TableCell>
                        {offer.max_active_sessions !== null ? offer.max_active_sessions : '∞'}
                      </TableCell>
                      <TableCell>
                        {offer.clients.length > 1 ? (
                          <Badge color="primary" badgeContent={offer.clients.length}>
                            <IconButton
                              color="secondary"
                              size="small"
                              style={{ margin: '-6px 0' }}
                              onClick={() => handleEditClients(offer)}
                            >
                              <IconEditClients />
                            </IconButton>
                          </Badge>
                        ) : (
                          <>
                            <IconButton
                              color="secondary"
                              size="small"
                              style={{ margin: '-6px 0' }}
                              onClick={() => handleEditClients(offer)}
                            >
                              <IconEditClients />
                            </IconButton>
                            {offer.clients.map((client, key) => {
                              if (client === null) {
                                return <em key={key} style={{ marginLeft: 4, lineHeight: '21px' }}>{t('allClients')}</em>
                              }

                              return <span key={key} style={{ marginLeft: 4, lineHeight: '21px' }}>{client}</span>
                            })}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="center" style={{ width: 62 }}>
                        <Tooltip title={`#: ${offer.id}`} placement={'top'} arrow interactive>
                          <IconInfo fontSize="small" style={{ verticalAlign: 'middle' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell style={{ paddingLeft: 0, width: 46 }}>
                        <IconButton
                          color="secondary"
                          size="small"
                          style={{ margin: '-5px 0px' }}
                          onClick={() => handleEdit(offer)}
                        >
                          <IconEdit />
                        </IconButton>
                      </TableCell>
                    </ListTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ListTablePagination
            count={offers.total}
            rowsPerPage={listPerPage}
            page={listPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <ClientsDrawer data={clientsDrawerData} />
          <EditSubscriptionOfferDialog data={editSubscriptionOfferDialogData} />
        </>
      )}
    </>
  )
}

export default List
